<template>
  <div :class="['webinar__chat', { 'webinar__chat--open': openChat }]">
    <div class="webinar__chat__open" @click="openChat = !openChat">
      <icon-message/>
    </div>
    <div class="webinar__chat__header">{{ t('public.comments') }}
      {{ messages.length ? `(${messages.length})` : '' }}
      <div class="webinar__chat__close" @click="openChat = false"></div>
    </div>
    <div class="webinar__chat__body">
      <div class="webinar__chat__wait" v-if="!started">
        {{ t('public.waitStart') }}
      </div>
      <template v-else>
        <div class="webinar__chat__messages" ref="messagesBlock">
          <ChatMessage v-for="message in messages" :key="message.id"
                       :message="message" :archived="archived"
                       @deleted="removeMessage(message.id)"
          />
        </div>
      </template>
    </div>
    <div class="webinar__chat__footer" v-if="!disabled && !archived">
      <div :class="inputClass" ref="editable"
           :contenteditable="!sending" @input="onInput" @blur="onInput"
           @keydown.enter.prevent="sendMessage"></div>
      <div class="webinar__chat__placeholder">
        {{ t('public.commentPlaceholder') }}
      </div>
      <div class="webinar__chat__send" @click="sendMessage">
        <icon-send/>
      </div>
      <div v-if="messageCounter <= 50" class="webinar__chat__counter">
        {{ messageCounter }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, nextTick, onBeforeUnmount, PropType, ref, watch,
} from 'vue'
import axios from 'axios'
import { Message } from '@/types/webinar.d'
import IconSend from '@/vue/icons/IconSend.vue'
import IconMessage from '@/vue/icons/IconMessage.vue'
import { useI18n } from 'vue-i18n'
import periodic, { CancelFunc } from '@/scripts/periodic'
import { request } from '@/scripts/combinedRequest'
import sleep from '@/scripts/sleep'
import ChatMessage from './ChatMessage.vue'

const GETTER_PERIOD = 5000

export default defineComponent({
  name: 'Chat',
  props: {
    scheduleId: {
      type: Number as PropType<number>,
      required: true,
    },
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    started: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    archived: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChatMessage,
    IconSend,
    IconMessage,
  },
  setup(props) {
    const { t } = useI18n()

    const messages = ref<Message[]>([])
    const messageIdList = computed(() => messages.value.map((m) => m.id))
    const lastTiming = computed(() => {
      const l = messages.value.length
      return l ? messages.value[l - 1].timing : 0
    })

    const messagesBlock = ref<HTMLElement>()

    const openChat = ref(false)
    const sending = ref(false)
    const message = ref('')

    const editable = ref<HTMLElement>()

    const messageLimit = 255
    watch(message, () => {
      if (message.value.length > 255) {
        message.value = message.value.slice(0, 255)
        if (editable.value) {
          editable.value.innerText = message.value
          if (editable.value?.lastChild) {
            const range = document.createRange()
            range.setStart(editable.value.lastChild, (editable.value.lastChild as Text).length)
            const selection = document.getSelection()
            selection?.removeAllRanges()
            selection?.addRange(range)
          }
        }
      }
    })
    const messageCounter = computed(() => messageLimit - message.value.length)

    // MESSAGES REMOVER
    onBeforeUnmount(periodic(15000, async () => {
      const getIdList = await request<number[]>('messages_id')
      messages.value = messages.value.filter((m: Message) => getIdList.includes(m.id))
    }))

    // MESSAGES GETTER
    let cancelGetter: CancelFunc

    function runGetter() {
      if (cancelGetter) {
        cancelGetter()
      }
      cancelGetter = periodic(GETTER_PERIOD, async () => {
        const timing = lastTiming.value
        let newMessages = await request<Message[]>('messages', timing)
        newMessages = newMessages
          .filter((m: Message) => !messageIdList.value?.includes(m.id))
          .map((m: Message) => Object.assign(m, { timestamp: new Date(m.timestamp) }))
        if (newMessages.length) {
          const delta = newMessages[newMessages.length - 1].timing - newMessages[0].timing
          if (delta >= (GETTER_PERIOD / 1000)) {
            messages.value.push(...newMessages)
            await nextTick(() => {
              if (messagesBlock.value) {
                messagesBlock.value.scrollTo(0, messagesBlock.value.scrollHeight)
              }
            })
          } else {
            nextTick(async () => {
              let previousTiming = 0
              for (let i = 0; i < newMessages.length; i++) {
                const m = newMessages[i]
                if (previousTiming) {
                  const d = m.timing - previousTiming
                  if (d >= 0) {
                    // eslint-disable-next-line no-await-in-loop
                    await sleep(d * 1000)
                  }
                }
                previousTiming = m.timing
                messages.value.push(m)
                // eslint-disable-next-line no-await-in-loop
                await nextTick(() => {
                  if (messagesBlock.value) {
                    messagesBlock.value.scrollTo(0, messagesBlock.value.scrollHeight)
                  }
                })
              }
            })
          }
        }
      })
    }

    runGetter()

    onBeforeUnmount(() => {
      if (cancelGetter) {
        cancelGetter()
      }
    })

    return {
      t,

      openChat,
      messages,
      sending,
      message,
      messagesBlock,
      editable,
      messageCounter,

      onInput() {
        message.value = editable.value ? editable.value.innerText : ''
      },
      async sendMessage() {
        if (!sending.value) {
          const m = message.value.trim()
          if (m) {
            sending.value = true
            await axios.post(`/messages/${props.scheduleId}`, { message: m })
            message.value = ''
            if (editable.value) {
              editable.value.innerText = ''
            }
            runGetter()
            await nextTick(() => {
              sending.value = false
              if (editable.value) {
                editable.value.focus()
              }
            })
          }
        }
      },
      removeMessage(id: number) {
        messages.value = messages.value.filter((m: Message) => id !== m.id)
      },

      inputClass: computed(() => [
        'webinar__chat__input',
        {
          'webinar__chat__input--not-empty': !!message.value,
          'webinar__chat__input--sending': sending.value,
        },
      ]),
    }
  },
})
</script>

<style lang="stylus">
.webinar__chat
  background var(--white-bg-color)
  display flex
  flex-direction column
  absolute top right
  size 100%
  transform translateX(100%)
  transition .2s
  box-shadow 0 3px 15px 3px rgba(27, 38, 56, .1)
  z-index 30

  &--open
    transform translateX(0)

  &__open
    absolute left -50px top
    background var(--white-bg-color)
    box-shadow 0 3px 15px 3px rgba(27, 38, 56, .1)
    border-radius 5px 0 0 5px
    size 50px
    display flex
    align-items center
    justify-content center
    z-index -1

    svg
      size 1.5rem

  &__counter
    absolute right 17px bottom 10px
    color var(--button-color)
    font-size .7em

  &__close
    absolute top right
    size 30px

    &::before
    &::after
      content ''
      width 20px
      border 1px solid
      absolute left 50% top 50%
      border-radius 1px
      margin-left -10px

    &::before
      transform rotate(45deg)

    &::after
      transform rotate(-45deg)

  &__header
    height 30px
    font-size .86em
    font-weight bold
    display flex
    align-items center
    padding 0 15px
    color var(--grey-text-color)
    box-shadow 0 0 15px 3px rgba(27, 38, 56, .1)
    flex 0 0 auto

  &__body
    height 100%
    position relative

  &__wait
    absolute left top
    size 100%
    display flex
    align-items center
    justify-content center
    text-align center
    padding 20px

  &__messages
    absolute left bottom
    width 100%
    max-height 100%
    overflow auto
    padding 10px

  &__footer
    padding 10px 15px
    box-shadow 0 0 15px 3px rgba(27, 38, 56, .1)
    position relative

  &__placeholder
    opacity .3
    absolute top 22px left 25px
    line-height 1.14
    pointer-events none

  &__send
    absolute right 25px bottom 21px
    size 21px 18px
    color var(--dark-bg-color)
    opacity .3
    transition .2s

  &__input
    min-height 40px
    border-radius 3px
    background var(--input-bg-color)
    padding 9px 41px 9px 10px
    line-height 20px
    border 1px solid transparent

    &:focus
    &--not-empty
      & ~ .webinar__chat__placeholder
        display none

    &:focus
      border-color var(--input-border-color)

    &--not-empty
      & ~ .webinar__chat__send
        cursor pointer
        color var(--button-color)
        opacity 1

    &--sending
      opacity .5
      cursor wait

      & ~ .webinar__chat__send
        cursor wait
        color var(--button-color)
        opacity 1
        animation rotation-z .5s linear infinite

@keyframes rotation-z
  0%
    transform rotate3d(1, 0, 0, 0deg)
  50%
    transform rotate3d(1, 0, 0, 90deg)
  100%
    transform rotate3d(1, 0, 0, 0deg)

@media screen and (min-width: 940px)
  .webinar__chat
    position static
    border-radius 5px
    transform translateX(0)

    &--open
      transform translateX(0)

    &__open
    &__close
      display none
</style>
